// src/lib/goalDomainRegistry.ts
//
// BekalNanti — Goal Domain Registry & Capability Framework
// Implements complete, continuous planning semantics for all 10 supported goal types:
// 1. education
// 2. retirement
// 3. emergency
// 4. housing
// 5. vehicle
// 6. hajj_umrah
// 7. wedding
// 8. business
// 9. vacation
// 10. other / notsure

import { CostCategory, CostFrequency } from './costFunding'

export interface DomainCostCategoryOption {
  id: CostCategory
  label: string
  description: string
  defaultFrequency: CostFrequency
  typicalItems?: string[]
}

export interface DomainStageOption {
  id: string
  label: string
  shortLabel: string
  defaultDurationMonths: number
  description: string
}

export interface GoalDomainDefinition {
  goalType: string
  title: string
  englishLabel: string
  iconName: string
  accentColor: string
  bgLight: string
  description: string
  
  // Step 1: Target Specification Configuration
  targetStageLabel: string // e.g., "Jenjang Pendidikan", "Tahap Usaha", "Tipe Properti"
  targetStagePlaceholder: string
  programNameLabel: string // e.g., "Program Studi", "Bidang Usaha", "Tipe / Nama Kendaraan"
  programNamePlaceholder: string
  
  // Step 2: Location & Institution Semantics
  showInstitution: boolean // true only for education
  locationTitle: string // Step 2 header title
  locationLabel: string // e.g., "Negara Sasaran", "Kota Operasional", "Destinasi"
  locationPlaceholder: string
  locationDescription: string // domain-specific explanation for step 2
  commonCities?: string[] // quick chips for common Indonesian cities / destinations
  
  // Step 3: Timing & Horizon Semantics
  targetYearLabel: string // e.g., "Tahun Mulai Studi", "Target Tahun Peluncuran", "Target Pensiun"
  targetYearDescription: string // domain-specific explanation for step 3
  durationLabel: string // e.g., "Durasi Program (Bulan)", "Durasi Persiapan (Bulan)", "Horizon Pensiun (Bulan)"
  durationDescription: string // explanation of what duration means for this domain
  
  // Step 4: Review & Readiness Semantics
  readinessKeyParameters: string // e.g., "jenjang pendidikan, bidang studi, negara, dan tahun mulai" vs "tahap usaha, bidang bisnis, lokasi operasional, dan target tahun peluncuran"
  
  stages: DomainStageOption[]
  commonPrograms: string[]
  
  // Cost Categories
  costCategories: DomainCostCategoryOption[]
  
  // Research Guidance
  defaultResearchQuery: (institutionOrTarget: string, programOrType?: string) => string
  suggestedResearchCategories: string[]
}

export const GOAL_DOMAINS: Record<string, GoalDomainDefinition> = {
  education: {
    goalType: 'education',
    title: 'Pendidikan',
    englishLabel: 'Education',
    iconName: 'GraduationCap',
    accentColor: '#0284C7',
    bgLight: '#F0F9FF',
    description: 'Perencanaan jenjang studi, biaya kuliah, biaya hidup mahasiswa, dan beasiswa.',
    targetStageLabel: 'Jenjang Pendidikan Sasaran',
    targetStagePlaceholder: 'Pilih jenjang (S1, S2, Diploma, dll)',
    programNameLabel: 'Bidang Studi / Peminatan',
    programNamePlaceholder: 'Misal: Teknik Informatika, Kedokteran, Bisnis',
    showInstitution: true,
    locationTitle: 'Negara & Institusi Pendidikan Sasaran',
    locationLabel: 'Negara Sasaran',
    locationPlaceholder: 'Pilih negara atau kota institusi pendidikan',
    locationDescription: 'Biaya pendidikan dan biaya hidup mahasiswa sangat bergantung pada negara dan kota institusi tujuan.',
    commonCities: ['Bandung', 'Yogyakarta', 'Depok / Jakarta', 'Surabaya', 'Semarang', 'Malang'],
    targetYearLabel: 'Target Tahun Mulai Studi',
    targetYearDescription: 'Tahun berapa pendidikan ini diperkirakan dimulai? (Dibutuhkan untuk menghitung horizon investasi & inflasi biaya pendidikan).',
    durationLabel: 'Durasi Pendidikan (Bulan)',
    durationDescription: 'Lama studi hingga kelulusan (contoh: S1 umumnya 48 bulan / 4 tahun).',
    readinessKeyParameters: 'jenjang pendidikan, bidang studi, negara, dan tahun mulai studi',
    stages: [
      { id: 's1', label: 'S1 / Sarjana', shortLabel: 'S1', defaultDurationMonths: 48, description: 'Pendidikan sarjana perguruan tinggi (4 tahun)' },
      { id: 's2', label: 'S2 / Magister', shortLabel: 'S2', defaultDurationMonths: 24, description: 'Pendidikan pascasarjana / master (2 tahun)' },
      { id: 'diploma', label: 'Diploma (D3/D4)', shortLabel: 'Diploma', defaultDurationMonths: 36, description: 'Pendidikan vokasi terapan (3-4 tahun)' },
      { id: 'sma', label: 'SMA / SMK', shortLabel: 'SMA/SMK', defaultDurationMonths: 36, description: 'Pendidikan menengah atas (3 tahun)' },
      { id: 'smp', label: 'SMP', shortLabel: 'SMP', defaultDurationMonths: 36, description: 'Pendidikan menengah pertama (3 tahun)' },
      { id: 'sd', label: 'SD', shortLabel: 'SD', defaultDurationMonths: 72, description: 'Pendidikan dasar (6 tahun)' },
      { id: 'course', label: 'Kursus & Sertifikasi', shortLabel: 'Kursus', defaultDurationMonths: 12, description: 'Pelatihan profesional atau keahlian khusus' },
    ],
    commonPrograms: [
      'Kedokteran & Kesehatan',
      'Teknik Informatika & Ilmu Komputer',
      'Bisnis, Manajemen & Keuangan',
      'Hukum & Hubungan Internasional',
      'Desain Komunikasi Visual & Seni',
      'Sains & Matematika',
      'Pendidikan & Keguruan',
    ],
    costCategories: [
      { id: 'tuition', label: 'Uang Kuliah & Biaya Pendidikan', description: 'SPP berkala, UKT, biaya SKS, atau pendaftaran', defaultFrequency: 'per_year', typicalItems: ['SPP Semester/Tahunan', 'Uang Pangkal / Gedung'] },
      { id: 'living', label: 'Tempat Tinggal & Biaya Hidup', description: 'Sewa kost/asrama, makan harian, utilitas', defaultFrequency: 'per_year', typicalItems: ['Asrama Kampus', 'Makan & Transportasi'] },
      { id: 'travel', label: 'Transportasi & Relokasi', description: 'Tiket perjalanan awal, kepulangan libur', defaultFrequency: 'one_off' },
      { id: 'admin_visa', label: 'Visa & Dokumen Legal', description: 'Paspor, visa studi, sertifikasi bahasa', defaultFrequency: 'one_off' },
      { id: 'equipment', label: 'Buku & Perangkat Belajar', description: 'Laptop, buku teks, alat praktikum', defaultFrequency: 'one_off' },
      { id: 'insurance', label: 'Asuransi & Kesehatan', description: 'Asuransi kesehatan mahasiswa wajib', defaultFrequency: 'per_year' },
      { id: 'other', label: 'Biaya Terkait Lainnya', description: 'Kebutuhan pendidikan lainnya', defaultFrequency: 'one_off' },
    ],
    defaultResearchQuery: (target, prog) => `Find current tuition fees and living costs for ${prog || 'undergraduate'} at ${target}.`,
    suggestedResearchCategories: ['tuition', 'living_cost', 'scholarship'],
  },

  business: {
    goalType: 'business',
    title: 'Usaha & Bisnis',
    englishLabel: 'Business',
    iconName: 'Store',
    accentColor: '#4F46E5',
    bgLight: '#EEF2FF',
    description: 'Perencanaan modal awal, legalitas, peralatan, dan modal kerja bisnis keluarga.',
    targetStageLabel: 'Tahap & Skala Bisnis',
    targetStagePlaceholder: 'Pilih tahap (Rintisan Awal, Ekspansi Cabang, dll)',
    programNameLabel: 'Bidang / Model Bisnis',
    programNamePlaceholder: 'Misal: Kafe F&B, Jasa Kreatif, Retail Toko, Agrobisnis',
    showInstitution: false,
    locationTitle: 'Wilayah Operasional & Lokasi Usaha',
    locationLabel: 'Kota / Wilayah Tempat Usaha',
    locationPlaceholder: 'Misal: Jakarta Selatan, Bandung, Surabaya, Online/Remote',
    locationDescription: 'Biaya sewa tempat, perizinan daerah, dan biaya operasional awal sangat bergantung pada kota operasional usaha.',
    commonCities: [
      'Jakarta Selatan',
      'Jakarta Pusat',
      'Bandung',
      'Surabaya',
      'Yogyakarta',
      'Tangerang',
      'Bekasi',
      'Medan',
      'Bali / Denpasar',
      'Online / Remote',
    ],
    targetYearLabel: 'Target Tahun Peluncuran / Operasional',
    targetYearDescription: 'Tahun berapa usaha atau ekspansi cabang ini direncanakan mulai beroperasi? (Dibutuhkan untuk menghitung horizon modal & inflasi peralatan).',
    durationLabel: 'Durasi Persiapan / Fase Awal (Bulan)',
    durationDescription: 'Estimasi waktu persiapan tempat, izin, dan pengadaan hingga bisnis mulai menghasilkan arus kas mandiri.',
    readinessKeyParameters: 'tahap usaha, bidang bisnis, lokasi operasional, dan tahun mulai operasional',
    stages: [
      { id: 'startup', label: 'Rintisan Awal (Startup / Baru)', shortLabel: 'Rintisan Baru', defaultDurationMonths: 12, description: 'Pendirian usaha baru dari nol hingga peluncuran' },
      { id: 'expansion', label: 'Ekspansi Cabang / Skala', shortLabel: 'Ekspansi', defaultDurationMonths: 12, description: 'Pembukaan cabang baru atau penambahan kapasitas' },
      { id: 'renovation', label: 'Modernisasi & Re-Branding', shortLabel: 'Modernisasi', defaultDurationMonths: 6, description: 'Renovasi tempat, teknologi, dan peralatan baru' },
      { id: 'franchise', label: 'Waralaba / Kemitraan', shortLabel: 'Franchise', defaultDurationMonths: 12, description: 'Pengambilan lisensi bisnis atau kemitraan waralaba' },
    ],
    commonPrograms: [
      'Kuliner / Food & Beverage (F&B)',
      'Retail & Toko Kelontong Modern',
      'Agrobisnis & Peternakan',
      'Jasa Kreatif & Agensi Digital',
      'Klinik & Layanan Kesehatan Mandiri',
      'Bengkel & Otomotif',
      'Pendidikan Non-Formal & Bimbingan Belajar',
    ],
    costCategories: [
      { id: 'equipment', label: 'Peralatan & Mesin Usaha', description: 'Mesin produksi, etalase, kompor, meja kursi, komputer kasir', defaultFrequency: 'one_off', typicalItems: ['Peralatan Dapur/Mesin', 'Perangkat POS & Komputer'] },
      { id: 'living', label: 'Sewa Tempat & Renovasi', description: 'Sewa ruko/tempat usaha tahunan, renovasi interior', defaultFrequency: 'per_year', typicalItems: ['Sewa Ruko Tahunan', 'Biaya Dekorasi & Partisi'] },
      { id: 'tuition', label: 'Modal Kerja Awal (Working Capital)', description: 'Stok persediaan bahan baku awal, gaji 3 bulan pertama', defaultFrequency: 'one_off', typicalItems: ['Bahan Baku Awal', 'Kas Operasional Awal'] },
      { id: 'admin_visa', label: 'Izin, Legalitas & Sertifikasi', description: 'NIB, izin edar BPOM, sertifikasi halal, akta notaris', defaultFrequency: 'one_off', typicalItems: ['Perizinan OSS & NIB', 'Sertifikasi Halal/BPOM'] },
      { id: 'other', label: 'Pemasaran & Peluncuran Awal', description: 'Plang nama, materi promosi, kampanye pembukaan', defaultFrequency: 'one_off', typicalItems: ['Signage Toko', 'Iklan Promosi Peluncuran'] },
    ],
    defaultResearchQuery: (target, prog) => `Find current setup costs, licensing requirements, and capital components for opening a ${prog || 'small business'} in ${target || 'Indonesia'}.`,
    suggestedResearchCategories: ['tuition', 'living_cost', 'admission_requirement'],
  },

  retirement: {
    goalType: 'retirement',
    title: 'Pensiun Mandiri',
    englishLabel: 'Retirement',
    iconName: 'Sunset',
    accentColor: '#CA8A04',
    bgLight: '#FEFCE8',
    description: 'Akumulasi dana hari tua dan ketahanan nafkah pasca-usia produktif keluarga.',
    targetStageLabel: 'Gaya Hidup & Pola Pensiun',
    targetStagePlaceholder: 'Pilih gaya hidup (Sederhana Mandiri, Aktif Berkelana, dll)',
    programNameLabel: 'Rencana Aktivitas / Tempat Menetap',
    programNamePlaceholder: 'Misal: Menetap di Rumah Utama, Berkebun di Kampung Halaman',
    showInstitution: false,
    locationTitle: 'Kota Domisili Hari Tua',
    locationLabel: 'Kota Domisili Pensiun',
    locationPlaceholder: 'Misal: Yogyakarta, Solo, Malang, Bandung, Bali',
    locationDescription: 'Biaya hidup harian dan akses layanan kesehatan hari tua sangat dipengaruhi oleh kota tempat menetap.',
    commonCities: ['Yogyakarta', 'Solo / Surakarta', 'Malang', 'Bandung', 'Bogor', 'Bali', 'Semarang', 'Surabaya'],
    targetYearLabel: 'Target Tahun Mulai Pensiun',
    targetYearDescription: 'Tahun berapa masa pensiun direncanakan dimulai? (Dibutuhkan untuk menghitung sisa waktu produktif untuk akumulasi modal).',
    durationLabel: 'Horizon Masa Pensiun (Bulan)',
    durationDescription: 'Estimasi rentang waktu hari tua yang dibiayai (contoh: 240 bulan / 20 tahun hidup mandiri).',
    readinessKeyParameters: 'gaya hidup pensiun, rencana aktivitas, kota domisili, dan tahun mulai pensiun',
    stages: [
      { id: 'modest', label: 'Pensiun Mandiri Nyaman', shortLabel: 'Mandiri Nyaman', defaultDurationMonths: 240, description: 'Memenuhi kebutuhan hidup sehari-hari, kesehatan, dan hobi ringan (20 tahun horizon)' },
      { id: 'simple', label: 'Pensiun Sederhana Asri', shortLabel: 'Sederhana', defaultDurationMonths: 240, description: 'Fokus pada ketenangan, kebutuhan pokok, dan komunitas lokal' },
      { id: 'active', label: 'Pensiun Aktif & Sosial', shortLabel: 'Aktif Sosial', defaultDurationMonths: 240, description: 'Termasuk anggaran sosial, silaturahmi keluarga, dan traveling berkala' },
    ],
    commonPrograms: [
      'Menetap di Rumah Utama',
      'Kembali ke Kampung Halaman',
      'Berkebun & Wirausaha Santai',
      'Aktivitas Sosial & Keagamaan',
    ],
    costCategories: [
      { id: 'living', label: 'Biaya Hidup Bulanan Hari Tua', description: 'Estimasi belanja dapur, utilitas rumah, pemeliharaan', defaultFrequency: 'per_year', typicalItems: ['Kebutuhan Pangan & Rumah', 'Pemeliharaan Tempat Tinggal'] },
      { id: 'insurance', label: 'Kesehatan & Rawat Lansia', description: 'BPJS, asuransi kesehatan swasta, dana darurat medis khusus', defaultFrequency: 'per_year', typicalItems: ['Iuran Jaminan Kesehatan', 'Suplemen & Cek Medis Rutin'] },
      { id: 'other', label: 'Dana Cadangan & Silaturahmi', description: 'Anggaran cucu, hari raya, dan sosial keagamaan', defaultFrequency: 'per_year', typicalItems: ['Anggaran Mudik & Hari Raya'] },
    ],
    defaultResearchQuery: (target) => `Find average monthly living costs and healthcare expenses for retirees in ${target || 'Indonesia'}.`,
    suggestedResearchCategories: ['living_cost', 'tuition'],
  },

  emergency: {
    goalType: 'emergency',
    title: 'Dana Darurat & Keamanan',
    englishLabel: 'Emergency Fund',
    iconName: 'ShieldCheck',
    accentColor: '#0EA5A4',
    bgLight: '#F0FDFA',
    description: 'Penyangga ketahanan keluarga menghadapi disrupsi arus kas, sakit, atau krisis.',
    targetStageLabel: 'Tingkat Ketahanan Dana',
    targetStagePlaceholder: 'Pilih kelipatan pengeluaran (3 Bulan, 6 Bulan, 12 Bulan)',
    programNameLabel: 'Tujuan Alokasi Khusus',
    programNamePlaceholder: 'Misal: Dana Siaga Medis & Pekerjaan Rumah Tangga',
    showInstitution: false,
    locationTitle: 'Lembaga & Instrumen Likuid',
    locationLabel: 'Tempat Penyimpanan Dana',
    locationPlaceholder: 'Misal: Rekening Bank Terpisah, Deposito Jangka Pendek, Reksadana Pasar Uang',
    locationDescription: 'Pilihan lembaga atau instrumen keuangan likuid berisiko rendah yang mudah dicairkan saat krisis atau kondisi darurat.',
    commonCities: ['Rekening Bank Operasional Terpisah', 'Deposito Bank Digital', 'Reksadana Pasar Uang (RPU)', 'Tabungan Emas Likuid'],
    targetYearLabel: 'Target Tahun Dana Darurat Terkumpul',
    targetYearDescription: 'Tahun berapa target dana darurat keluarga ini harus terkumpul secara utuh? (Dibutuhkan untuk menghitung alokasi tabungan bulanan).',
    durationLabel: 'Horizon Pengumpulan Dana (Bulan)',
    durationDescription: 'Target jangka waktu menghimpun dana cadangan hingga mencapai kelipatan pengeluaran yang ditargetkan.',
    readinessKeyParameters: 'tingkat ketahanan, alokasi khusus, instrumen penyimpanan, dan target tahun tercapai',
    stages: [
      { id: '3_months', label: '3 Kali Pengeluaran Bulanan', shortLabel: '3 Bulan', defaultDurationMonths: 12, description: 'Ketahanan dasar untuk pekerja tetap tanpa tanggungan berat' },
      { id: '6_months', label: '6 Kali Pengeluaran Bulanan', shortLabel: '6 Bulan', defaultDurationMonths: 24, description: 'Standar emas ketahanan rumah tangga dengan anak' },
      { id: '12_months', label: '12 Kali Pengeluaran Bulanan', shortLabel: '12 Bulan', defaultDurationMonths: 36, description: 'Ketahanan ekstra untuk wirausahawan atau pekerja lepas' },
    ],
    commonPrograms: [
      'Penyangga Krisis Pekerjaan',
      'Dana Siaga Kesehatan Tambahan',
      'Penyangga Perbaikan Rumah Darurat',
    ],
    costCategories: [
      { id: 'tuition', label: 'Target Akumulasi Pokok Cadangan', description: 'Nominal total dana siaga yang wajib terkumpul di instrumen likuid', defaultFrequency: 'one_off', typicalItems: ['Target Total Dana Siaga'] },
      { id: 'other', label: 'Alokasi Penggantian Berkala', description: 'Cadangan pemulihan jika dana sempat terpakai sebagian', defaultFrequency: 'per_year' },
    ],
    defaultResearchQuery: (target) => `Find financial planning standards for household emergency fund reserve in ${target || 'Indonesia'}.`,
    suggestedResearchCategories: ['living_cost', 'tuition'],
  },

  housing: {
    goalType: 'housing',
    title: 'Rumah & Tempat Tinggal',
    englishLabel: 'Housing',
    iconName: 'Home',
    accentColor: '#EA580C',
    bgLight: '#FFF7ED',
    description: 'Kepemilikan hunian pertama, uang muka KPR, atau renovasi rumah keluarga.',
    targetStageLabel: 'Jenis Transaksi Hunian',
    targetStagePlaceholder: 'Pilih jenis (Beli Tunai, Uang Muka KPR, Bangun/Renovasi)',
    programNameLabel: 'Tipe & Spesifikasi Properti',
    programNamePlaceholder: 'Misal: Rumah Tipe 45/90, Apartemen 2 Kamar, Renovasi Atap',
    showInstitution: false,
    locationTitle: 'Wilayah & Lokasi Properti',
    locationLabel: 'Kota / Wilayah Target Hunian',
    locationPlaceholder: 'Misal: Tangerang Selatan, Bogor, Sleman, Surabaya Barat, Bekasi',
    locationDescription: 'Harga tanah, nilai appraisal properti, dan pajak BPHTB/notaris sangat ditentukan oleh kota dan kecamatan lokasi hunian.',
    commonCities: ['Tangerang Selatan', 'Kab. Tangerang', 'Kota Bogor', 'Kab. Bogor', 'Bekasi', 'Depok', 'Sleman', 'Surabaya'],
    targetYearLabel: 'Target Tahun Transaksi / Akad KPR',
    targetYearDescription: 'Tahun berapa pembelian rumah, akad KPR, atau renovasi ditargetkan berlangsung? (Dibutuhkan untuk menghitung pertumbuhan nilai properti & horizon DP).',
    durationLabel: 'Horizon Persiapan Dana (Bulan)',
    durationDescription: 'Jangka waktu menabung untuk uang muka (DP), biaya renovasi, atau pelunasan tunai properti.',
    readinessKeyParameters: 'jenis transaksi hunian, tipe properti, kota target, dan tahun transaksi',
    stages: [
      { id: 'dp_kpr', label: 'Uang Muka KPR (DP 10-20%)', shortLabel: 'DP KPR', defaultDurationMonths: 24, description: 'Akumulasi uang muka pembelian rumah via pembiayaan bank' },
      { id: 'cash_purchase', label: 'Pembelian Rumah Tunai', shortLabel: 'Beli Tunai', defaultDurationMonths: 60, description: 'Pembelian unit hunian langsung lunas' },
      { id: 'renovation', label: 'Renovasi Besar / Tambah Lantai', shortLabel: 'Renovasi', defaultDurationMonths: 12, description: 'Peningkatan kapasitas dan kenyamanan hunian' },
      { id: 'build_land', label: 'Beli Tanah & Bangun Mandiri', shortLabel: 'Bangun Mandiri', defaultDurationMonths: 36, description: 'Pembelian kaveling tanah dilanjutkan pembangunan fisik' },
    ],
    commonPrograms: [
      'Rumah Tapak Baru Developer',
      'Rumah Sekunder / Bekas Layak Huni',
      'Apartemen Keluarga Dekat Transportasi',
      'Renovasi Kamar Anak & Dapur',
    ],
    costCategories: [
      { id: 'tuition', label: 'Nilai Pokok / Uang Muka (DP)', description: 'Total nilai pembelian atau uang muka yang harus disetor', defaultFrequency: 'one_off', typicalItems: ['Uang Muka (DP) Properti', 'Pelunasan Pokok'] },
      { id: 'admin_visa', label: 'Pajak & Biaya Legalitas (BPHTB/Notaris)', description: 'BPHTB 5%, AJB notaris, balik nama sertifikat, provisi KPR', defaultFrequency: 'one_off', typicalItems: ['BPHTB & Biaya Notaris', 'Biaya Asuransi Jiwa KPR'] },
      { id: 'equipment', label: 'Perabotan Awal & Interior', description: 'Peralatan esensial (tempat tidur, dapur, pendingin ruangan)', defaultFrequency: 'one_off', typicalItems: ['Furnitur Dasar Hunian'] },
      { id: 'other', label: 'Renovasi Minor & Serah Terima', description: 'Pagar, kanopi, atau perapian dinding awal', defaultFrequency: 'one_off' },
    ],
    defaultResearchQuery: (target) => `Find current property prices and acquisition tax components (BPHTB, notaris) in ${target || 'Jabodetabek'}.`,
    suggestedResearchCategories: ['tuition', 'living_cost', 'admission_requirement'],
  },

  vehicle: {
    goalType: 'vehicle',
    title: 'Kendaraan Keluarga',
    englishLabel: 'Vehicle',
    iconName: 'Car',
    accentColor: '#059669',
    bgLight: '#ECFDF5',
    description: 'Pengadaan mobil keluarga atau sepeda motor untuk mobilitas harian.',
    targetStageLabel: 'Kondisi & Jenis Kendaraan',
    targetStagePlaceholder: 'Pilih (Mobil Baru, Mobil Bekas Terawat, Motor)',
    programNameLabel: 'Merk / Model Kendaraan',
    programNamePlaceholder: 'Misal: MPV 7 Penumpang Keluarga, Motor Listrik',
    showInstitution: false,
    locationTitle: 'Wilayah Registrasi & Samsat',
    locationLabel: 'Kota / Wilayah Registrasi Plat Nomor',
    locationPlaceholder: 'Misal: DKI Jakarta (Plat B), Bandung (Plat D), Surabaya (Plat L)',
    locationDescription: 'Wilayah samsat dan domisili menentukan tarif Bea Balik Nama (BBNKB), Pajak Kendaraan Bermotor (PKB), dan biaya asuransi regional.',
    commonCities: ['DKI Jakarta (Plat B)', 'Jawa Barat (Plat D/F/E)', 'Jawa Timur (Plat L/N)', 'Jawa Tengah (Plat H/AD)', 'DI Yogyakarta (Plat AB)'],
    targetYearLabel: 'Target Tahun Pembelian Kendaraan',
    targetYearDescription: 'Tahun berapa kendaraan keluarga ini ditargetkan siap dibeli? (Dibutuhkan untuk memproyeksikan harga on-the-road dan alokasi DP).',
    durationLabel: 'Horizon Pengumpulan Dana (Bulan)',
    durationDescription: 'Lama waktu pengumpulan dana tunai atau uang muka (DP) kendaraan.',
    readinessKeyParameters: 'jenis kendaraan, merk/model, wilayah registrasi, dan tahun pembelian',
    stages: [
      { id: 'car_family', label: 'Mobil Keluarga (MPV/SUV)', shortLabel: 'Mobil Keluarga', defaultDurationMonths: 24, description: 'Kendaraan roda empat untuk mobilitas seluruh anggota keluarga' },
      { id: 'car_second', label: 'Mobil Bekas Berkualitas', shortLabel: 'Mobil Bekas', defaultDurationMonths: 12, description: 'Pilihan hemat fungsional dengan depresiasi rendah' },
      { id: 'motorcycle', label: 'Sepeda Motor Harian', shortLabel: 'Sepeda Motor', defaultDurationMonths: 12, description: 'Roda dua untuk mobilitas kerja dan sekolah' },
    ],
    commonPrograms: [
      'MPV 7-Seater Standar Keluarga',
      'Compact SUV / Crossover',
      'City Car Irit Bahan Bakar',
      'Motor Matic Komuter',
    ],
    costCategories: [
      { id: 'tuition', label: 'Harga Beli / Uang Muka Kendaraan', description: 'Total nilai tebus tunai atau uang muka leasing', defaultFrequency: 'one_off', typicalItems: ['Harga Beli OTR', 'Uang Muka Kendaraan'] },
      { id: 'insurance', label: 'Asuransi Kendaraan (All Risk/TLO)', description: 'Perlindungan tabrakan, bencana, dan kehilangan tahunan', defaultFrequency: 'per_year', typicalItems: ['Polis Asuransi All Risk'] },
      { id: 'admin_visa', label: 'Pajak & Balik Nama / BBNKB', description: 'Pajak tahunan kendaraan bermotor dan biaya administrasi STNK', defaultFrequency: 'one_off', typicalItems: ['Pajak Kendaraan Awal / BBN'] },
    ],
    defaultResearchQuery: (_target, prog) => `Find current market price and annual tax for ${prog || 'family car'} in Indonesia.`,
    suggestedResearchCategories: ['tuition', 'insurance'],
  },

  hajj_umrah: {
    goalType: 'hajj_umrah',
    title: 'Haji & Umrah',
    englishLabel: 'Hajj & Umrah',
    iconName: 'Compass',
    accentColor: '#10B981',
    bgLight: '#ECFDF5',
    description: 'Ibadah haji reguler/khusus dan ziarah umrah keluarga ke Baitullah.',
    targetStageLabel: 'Jenis Ibadah',
    targetStagePlaceholder: 'Pilih jenis (Haji Reguler, Haji Khusus/Furoda, Umrah Keluarga)',
    programNameLabel: 'Paket / Penyelenggara',
    programNamePlaceholder: 'Misal: Paket Umrah Ramadhan, Kuota Haji Kemenag, Haji Plus',
    showInstitution: false,
    locationTitle: 'Kota Embarkasi & Keberangkatan',
    locationLabel: 'Kota Embarkasi Bandara',
    locationPlaceholder: 'Misal: Jakarta (CGK), Surabaya (SUB), Solo (SOC), Medan (KNO)',
    locationDescription: 'Kota embarkasi menentukan bandara keberangkatan, maskapai penerbangan, dan regulasi kuota ibadah regional.',
    commonCities: ['Jakarta (CGK)', 'Surabaya (SUB)', 'Solo (SOC)', 'Medan (KNO)', 'Makassar (UPG)', 'Balikpapan (BPN)'],
    targetYearLabel: 'Target Tahun Keberangkatan Ibadah',
    targetYearDescription: 'Tahun berapa keberangkatan haji atau ziarah umrah keluarga ditargetkan berlangsung?',
    durationLabel: 'Estimasi Masa Tunggu / Persiapan (Bulan)',
    durationDescription: 'Estimasi masa antrean kuota haji atau rentang waktu menabung paket umrah keluarga.',
    readinessKeyParameters: 'jenis ibadah, paket penyelenggara, kota keberangkatan, dan tahun keberangkatan',
    stages: [
      { id: 'umrah_family', label: 'Umrah Keluarga (9-12 Hari)', shortLabel: 'Umrah Keluarga', defaultDurationMonths: 12, description: 'Ibadah umrah bersama pasangan dan anak' },
      { id: 'hajj_reguler', label: 'Haji Reguler (Antrean Kemenag)', shortLabel: 'Haji Reguler', defaultDurationMonths: 120, description: 'Pendaftaran porsi awal dan pelunasan bertahap (10-15 tahun)' },
      { id: 'hajj_plus', label: 'Haji Khusus / Plus (Antrean Pendek)', shortLabel: 'Haji Plus', defaultDurationMonths: 60, description: 'Penyelenggaraan haji khusus dengan masa tunggu lebih singkat' },
    ],
    commonPrograms: [
      'Umrah Reguler 9 Hari Bersama Keluarga',
      'Umrah Musim Ramadhan / Syawal',
      'Setoran Awal Porsi Haji Kemenag',
      'Pelunasan Bipih Haji Reguler',
    ],
    costCategories: [
      { id: 'tuition', label: 'Paket Perjalanan & Tiket Pesawat', description: 'Biaya paket travel haji/umrah, visa, dan penerbangan PP', defaultFrequency: 'one_off', typicalItems: ['Paket Travel & Tiket PP'] },
      { id: 'living', label: 'Uang Saku & Kebutuhan di Tanah Suci', description: 'Makan di luar paket, dam/hadyu, belanja oleh-oleh secukupnya', defaultFrequency: 'one_off', typicalItems: ['Uang Saku Selama Ibadah', 'Dam / Kurban'] },
      { id: 'admin_visa', label: 'Paspor, Vaksin & Perlengkapan', description: 'Pembuatan paspor 10 tahun, vaksin meningitis, kain ihram/koper', defaultFrequency: 'one_off', typicalItems: ['Vaksin & Paspor', 'Koper & Seragam Travel'] },
    ],
    defaultResearchQuery: () => `Find current official Biaya Perjalanan Ibadah Haji (Bipih) and Umrah packages officially recognized by Kemenag.`,
    suggestedResearchCategories: ['tuition', 'living_cost', 'admission_requirement'],
  },

  wedding: {
    goalType: 'wedding',
    title: 'Pernikahan & Keluarga',
    englishLabel: 'Wedding',
    iconName: 'HeartHandshake',
    accentColor: '#E11D48',
    bgLight: '#FFF1F2',
    description: 'Penyelenggaraan akad nikah, resepsi, dan bekal awal rumah tangga baru.',
    targetStageLabel: 'Skala Acara Pernikahan',
    targetStagePlaceholder: 'Pilih skala (Akad Intim, Resepsi Menengah, Resepsi Lengkap)',
    programNameLabel: 'Konsep / Rencana Acara',
    programNamePlaceholder: 'Misal: Akad & Syukuran Keluarga 100 Undangan, Resepsi Gedung',
    showInstitution: false,
    locationTitle: 'Kota Lokasi Acara Pernikahan',
    locationLabel: 'Kota Penyelenggaraan Acara',
    locationPlaceholder: 'Misal: Bandung, Jakarta Selatan, Yogyakarta, Surabaya, Solo',
    locationDescription: 'Lokasi kota dan wilayah acara menentukan standar tarif sewa gedung/venue, konsumsi katering per porsi, dan vendor pernikahan.',
    commonCities: ['Jakarta', 'Bandung', 'Yogyakarta', 'Surabaya', 'Solo', 'Semarang', 'Bogor', 'Malang'],
    targetYearLabel: 'Target Tahun Akad / Resepsi',
    targetYearDescription: 'Tahun berapa rangkaian acara pernikahan direncanakan diselenggarakan? (Dibutuhkan untuk menghitung estimasi inflasi vendor pernikahan).',
    durationLabel: 'Masa Persiapan Acara (Bulan)',
    durationDescription: 'Rentang waktu persiapan booking tempat, fiksasi vendor, dan pembayaran bertahap (contoh: 12-18 bulan).',
    readinessKeyParameters: 'skala acara pernikahan, konsep acara, kota acara, dan tahun pernikahan',
    stages: [
      { id: 'intimate', label: 'Akad Intim & Ramah Tamah (50-100 Orang)', shortLabel: 'Akad Intim', defaultDurationMonths: 12, description: 'Fokus pada kesakralan bersama keluarga inti dan sahabat terdekat' },
      { id: 'moderate', label: 'Resepsi Menengah (200-400 Undangan)', shortLabel: 'Menengah', defaultDurationMonths: 18, description: 'Resepsi gedung standar atau restoran keluarga' },
      { id: 'traditional', label: 'Pernikahan Adat Lengkap', shortLabel: 'Adat Lengkap', defaultDurationMonths: 24, description: 'Rangkaian prosesi adat tradisional dan resepsi besar' },
    ],
    commonPrograms: [
      'Akad Nikah Masjid & Restoran Intim',
      'Resepsi Gedung Standar',
      'Pernikahan Adat Tradisional',
      'Garden Party / Outdoor Ramah Tamah',
    ],
    costCategories: [
      { id: 'living', label: 'Katering & Sewa Tempat (Venue)', description: 'Biaya konsumsi undangan dan sewa gedung/restoran', defaultFrequency: 'one_off', typicalItems: ['Katering Prasmanan', 'Sewa Tempat Acara'] },
      { id: 'tuition', label: 'Dekorasi, Busana & Rias Pengantin', description: 'Panggung pelaminan, sewa gaun/kebaya, dan tata rias pengantin', defaultFrequency: 'one_off', typicalItems: ['Pelaminan & Bunga', 'Busana Pengantin & Orang Tua'] },
      { id: 'admin_visa', label: 'Dokumentasi, Undangan & Souvenir', description: 'Foto/video resepsi, cetak undangan atau digital, suvenir tamu', defaultFrequency: 'one_off', typicalItems: ['Fotografi & Video', 'Souvenir Undangan'] },
      { id: 'other', label: 'Administrasi KUA & Mahar Awal', description: 'Biaya pencatatan nikah KUA, mahar atau seserahan wajib', defaultFrequency: 'one_off', typicalItems: ['Pencatatan KUA', 'Mahar & Hantaran'] },
    ],
    defaultResearchQuery: (target) => `Find typical wedding catering and venue cost components in ${target || 'Indonesia'}.`,
    suggestedResearchCategories: ['living_cost', 'tuition'],
  },

  vacation: {
    goalType: 'vacation',
    title: 'Liburan & Perjalanan',
    englishLabel: 'Vacation',
    iconName: 'Palmtree',
    accentColor: '#0EA5A4',
    bgLight: '#F0FDFA',
    description: 'Wisata edukasi keluarga, liburan tahunan, atau rekreasi berharga.',
    targetStageLabel: 'Tipe Destinasi Liburan',
    targetStagePlaceholder: 'Pilih destinasi (Wisata Domestik, Asia Tenggara, Luar Negeri)',
    programNameLabel: 'Tujuan Wisata / Rute Utama',
    programNamePlaceholder: 'Misal: Liburan Labuan Bajo 4 Hari, Jepang Musim Semi',
    showInstitution: false,
    locationTitle: 'Destinasi & Kota Tujuan Wisata',
    locationLabel: 'Kota / Negara Destinasi Liburan',
    locationPlaceholder: 'Misal: Bali, Labuan Bajo, Jepang, Singapura, Turki, Swiss',
    locationDescription: 'Destinasi wisata menentukan tarif tiket transportasi pulang-pergi, kebutuhan visa turis, dan indeks biaya hidup liburan.',
    commonCities: ['Bali (Denpasar / Ubud)', 'Labuan Bajo (Komodo)', 'Yogyakarta', 'Lombok', 'Jepang (Tokyo / Osaka)', 'Singapura', 'Malaysia (Kuala Lumpur)', 'Turki (Istanbul)'],
    targetYearLabel: 'Target Tahun Keberangkatan Wisata',
    targetYearDescription: 'Tahun berapa liburan keluarga ini direncanakan terlaksana? (Dibutuhkan untuk menghitung horizon tabungan wisata).',
    durationLabel: 'Lama Perjalanan / Persiapan (Bulan)',
    durationDescription: 'Waktu persiapan tabungan tiket atau durasi perjalanan wisata keluarga.',
    readinessKeyParameters: 'tipe destinasi wisata, rute tujuan, destinasi target, dan tahun keberangkatan',
    stages: [
      { id: 'domestic', label: 'Wisata Domestik Nusantara', shortLabel: 'Domestik', defaultDurationMonths: 6, description: 'Jelajah destinasi wisata dalam negeri bersama keluarga' },
      { id: 'regional', label: 'Perjalanan Asia Tenggara / Regional', shortLabel: 'Regional ASEAN', defaultDurationMonths: 12, description: 'Wisata luar negeri bebas visa regional' },
      { id: 'international', label: 'Perjalanan Mancanegara Jauh', shortLabel: 'Internasional', defaultDurationMonths: 24, description: 'Wisata Eropa, Asia Timur, atau Timur Tengah' },
    ],
    commonPrograms: [
      'Liburan Keluarga ke Bali / Lombok',
      'Eksplorasi Labuan Bajo & Komodo',
      'Wisata Ramah Anak ke Singapura / Malaysia',
      'Wisata Budaya & Musim Gugur di Jepang',
    ],
    costCategories: [
      { id: 'tuition', label: 'Tiket Pesawat & Transportasi Utama', description: 'Penerbangan PP seluruh anggota keluarga dan kereta wisata', defaultFrequency: 'one_off', typicalItems: ['Tiket Pesawat Pulang Pergi'] },
      { id: 'living', label: 'Akomodasi Hotel & Makan Wisata', description: 'Sewa kamar hotel/resor dan kuliner khas destinasi', defaultFrequency: 'one_off', typicalItems: ['Hotel Menginap', 'Makan Selama Wisata'] },
      { id: 'other', label: 'Tiket Wahana & Atraksi Wisata', description: 'Tiket masuk taman bermain, museum, tur privat', defaultFrequency: 'one_off', typicalItems: ['Tiket Atraksi & Tur'] },
      { id: 'admin_visa', label: 'Visa Turis & Asuransi Perjalanan', description: 'Biaya visa turis dan asuransi proteksi perjalanan', defaultFrequency: 'one_off' },
    ],
    defaultResearchQuery: (target) => `Find estimated family travel costs, flight tickets, and hotel rates for ${target}.`,
    suggestedResearchCategories: ['tuition', 'living_cost'],
  },

  other: {
    goalType: 'other',
    title: 'Rencana Bebas & Khusus',
    englishLabel: 'Custom Goal',
    iconName: 'Target',
    accentColor: '#64748B',
    bgLight: '#F8FAFC',
    description: 'Perencanaan mandiri untuk tujuan keluarga yang dirancang secara fleksibel.',
    targetStageLabel: 'Tingkat Kesiapan Rencana',
    targetStagePlaceholder: 'Pilih tahap (Tahap Awal, Tahap Persiapan, Siap Eksekusi)',
    programNameLabel: 'Nama / Sub-Kategori Rencana',
    programNamePlaceholder: 'Misal: Pembangunan Musholla, Riset Mandiri, Proyek Keluarga',
    showInstitution: false,
    locationTitle: 'Lingkup & Wilayah Pelaksanaan',
    locationLabel: 'Wilayah / Kota Lingkup Rencana',
    locationPlaceholder: 'Misal: Nasional, Jakarta, Regional, atau Domestik',
    locationDescription: 'Lingkup geografis atau wilayah pelaksanaan rencana mandiri keluarga.',
    targetYearLabel: 'Target Tahun Realisasi Rencana',
    targetYearDescription: 'Tahun berapa target rencana ini ingin mulai direalisasikan?',
    durationLabel: 'Horizon Waktu Rencana (Bulan)',
    durationDescription: 'Jangka waktu persiapan atau pelaksanaan rencana mandiri keluarga.',
    readinessKeyParameters: 'tingkat kesiapan rencana, nama rencana, wilayah lingkup, dan tahun realisasi',
    stages: [
      { id: 'custom_std', label: 'Sasaran Bebas Terencana', shortLabel: 'Standar', defaultDurationMonths: 12, description: 'Tujuan kustom keluarga dengan target nominal terukur' },
      { id: 'custom_long', label: 'Proyek Jangka Menengah / Panjang', shortLabel: 'Jangka Panjang', defaultDurationMonths: 36, description: 'Tujuan khusus yang dicapai bertahap lebih dari 3 tahun' },
    ],
    commonPrograms: [
      'Proyek Khusus Keluarga',
      'Investasi Aset Berharga',
      'Kemandirian Komunitas',
    ],
    costCategories: [
      { id: 'tuition', label: 'Alokasi Dana Pokok Sasaran', description: 'Kebutuhan dana utama yang wajib terhimpun', defaultFrequency: 'one_off', typicalItems: ['Alokasi Pokok Rencana'] },
      { id: 'living', label: 'Biaya Pendukung Berkala', description: 'Pengeluaran operasional pendukung selama persiapan', defaultFrequency: 'per_year' },
      { id: 'other', label: 'Pengeluaran Terkait Lainnya', description: 'Pos biaya tambahan yang relevan', defaultFrequency: 'one_off' },
    ],
    defaultResearchQuery: (target) => `Find planning requirements and typical benchmarks for ${target}.`,
    suggestedResearchCategories: ['tuition', 'living_cost'],
  },
}

// Synonyms / Aliases to map any legacy or alternative goal types
const GOAL_TYPE_ALIASES: Record<string, string> = {
  usaha: 'business',
  pensiun: 'retirement',
  emergency_fund: 'emergency',
  darurat: 'emergency',
  rumah: 'housing',
  kendaraan: 'vehicle',
  haji: 'hajj_umrah',
  umrah: 'hajj_umrah',
  pernikahan: 'wedding',
  family: 'wedding',
  liburan: 'vacation',
  travel: 'vacation',
}

export function getGoalDomain(goalType?: string | null): GoalDomainDefinition {
  const norm = (goalType || '').trim().toLowerCase()
  const key = GOAL_TYPE_ALIASES[norm] ?? norm
  // Default fallback to 'other'
  return GOAL_DOMAINS[key] ?? GOAL_DOMAINS.other!
}
